import { useState, useEffect, useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import api from '@/services/api'
import type { Proposta } from '@/types'
import { normalizeProposta } from '@/utils/propostaPublicaUtils'
import {
  buildLegacyPlanItems,
  parsePropostaV2Dados,
} from '@/utils/propostaPublicaV2Items'
import type { AcceptClientData } from '@/components/PropostaPublicaV2/ProposalAcceptWizard'
import { getPlanCategory } from '@/components/PropostaPublicaV2/ProposalPlanSection'

export type PropostaV2Deal = {
  empresa?: { nome?: string; razaoSocial?: string; nomeFantasia?: string; cnpj?: string; responsavelAssinaturaNome?: string; responsavelAssinaturaEmail?: string; responsavelFinanceiroNome?: string; responsavelFinanceiroEmail?: string }
  closer?: { name?: string; email?: string; avatar?: string }
}

export function usePropostaPublicaV2Page(proposalId: string | undefined) {
  const [proposta, setProposta] = useState<Proposta | null>(null)
  const [deal, setDeal] = useState<PropostaV2Deal | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failedAvatar, setFailedAvatar] = useState(false)
  const [selectedPlans, setSelectedPlans] = useState<Set<number> | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const fetchProposal = useCallback(async () => {
    if (!proposalId) throw new Error('ID da proposta não informado.')
    const endpoints = [`/propostas/public/id/${proposalId}`, `/propostas/public/${proposalId}`]
    for (const endpoint of endpoints) {
      try {
        const { data } = await api.get(endpoint)
        const payload = data?.data ?? data
        const normalized = normalizeProposta(payload?.proposta ?? payload)
        setProposta(normalized)
        setDeal((payload?.deal ?? null) as PropostaV2Deal | null)
        try {
          await api.post(`/propostas/${proposalId}/track`, { action: 'visualizou' })
        } catch {
          /* best-effort */
        }
        return normalized
      } catch {
        /* next */
      }
    }
    throw new Error('Proposta não encontrada ou expirada.')
  }, [proposalId])

  useEffect(() => {
    void fetchProposal()
      .catch((err: Error) => setError(err.message))
      .finally(() => setLoading(false))
  }, [fetchProposal])

  const dados = useMemo(() => (proposta ? parsePropostaV2Dados(proposta) : null), [proposta])
  const itens = useMemo(() => (proposta ? buildLegacyPlanItems(proposta) : []), [proposta])

  const togglePlan = useCallback(
    (idx: number) => {
      if (!proposta || itens.length === 0) return
      const targetCat = getPlanCategory(itens[idx])
      setSelectedPlans((prev) => {
        const currentSet = prev ?? new Set([0])
        if (dados?.isCombo) return currentSet
        const newSet = new Set(currentSet)
        if (newSet.has(idx)) newSet.delete(idx)
        else {
          for (const existingIdx of newSet) {
            if (getPlanCategory(itens[existingIdx]) === targetCat) newSet.delete(existingIdx)
          }
          newSet.add(idx)
        }
        return newSet
      })
    },
    [proposta, itens, dados?.isCombo],
  )

  const handleOpenAccept = useCallback(
    (idx?: number) => {
      if (!proposta || proposta.status === 'aceita' || proposta.status === 'recusada') return
      if (idx !== undefined) togglePlan(idx)
      setIsModalOpen(true)
    },
    [proposta, togglePlan],
  )

  const handleConfirmAccept = useCallback(
    async (clientData: AcceptClientData) => {
      if (!proposalId) return
      await api.patch(`/propostas/${proposalId}/aceitar`, {
        signerName: clientData.respAssinaturaNome,
        signerEmail: clientData.respAssinaturaEmail,
        clientData,
      })
      await fetchProposal()
      setIsModalOpen(false)
      toast.success('Proposta aceita com sucesso!')
    },
    [proposalId, fetchProposal],
  )

  const activePlans = selectedPlans ?? new Set(itens.length > 0 ? [0] : [])
  const selectedItems = itens.filter((_, idx) => activePlans.has(idx))

  const totalSelected = selectedItems.reduce((sum, item) => {
    const isRec = item.tipo === 'recorrente' || item.tipo === 'mensal'
    const val = isRec && item.pricing?.installmentValue ? Number(item.pricing.installmentValue) : Number(item.valor || 0)
    return sum + val
  }, 0)

  const hasRecurring = selectedItems.some((i) => i.tipo === 'recorrente' || i.tipo === 'mensal')

  const statusInfo = useMemo(() => {
    if (!proposta || !dados) return { label: 'Ativa', cls: 'ativa' as const }
    const s = proposta.status?.toLowerCase()
    if (s === 'aceita') return { label: 'Aceita', cls: 'aceita' as const }
    if (s === 'recusada' || s === 'rejeitada') return { label: 'Recusada', cls: 'recusada' as const }
    const expires = new Date(proposta.createdAt)
    expires.setDate(expires.getDate() + Number(dados.condicaoPagamento.vigencia || 30))
    if (new Date() > expires) return { label: 'Expirada', cls: 'expirada' as const }
    const daysLeft = Math.ceil((expires.getTime() - Date.now()) / 86400000)
    return { label: `Ativa · ${daysLeft}d restantes`, cls: 'ativa' as const }
  }, [proposta, dados])

  const benefitsList = itens.some((i) => (i.nome || '').toLowerCase().includes('marketing'))
    ? ['Gestão mensal de campanhas Google e Meta', 'Especialistas certificados', 'Relatórios semanais', 'Suporte dedicado']
    : ['Projeto com escopo definido', 'Metodologia ágil', 'Equipe multidisciplinar', 'Suporte dedicado']

  const empresaNome =
    deal?.empresa?.nomeFantasia || deal?.empresa?.nome || deal?.empresa?.razaoSocial || 'Empresa Cliente'

  return {
    proposta,
    deal,
    loading,
    error,
    failedAvatar,
    setFailedAvatar,
    itens,
    dados,
    activePlans,
    selectedItems,
    totalSelected,
    hasRecurring,
    statusInfo,
    benefitsList,
    empresaNome,
    isModalOpen,
    setIsModalOpen,
    togglePlan,
    handleOpenAccept,
    handleConfirmAccept,
  }
}
